import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { NewsArticle, NewsStatus, NewsCategory } from '@municipality/types';
import { Timestamp } from 'firebase/firestore';

interface NewsStatistics {
  total: number;
  published: number;
  draft: number;
  archived: number;
  totalViews: number;
  averageViews: number;
  pinned: number;
  featured: number;
}

interface NewsListResponse {
  items: NewsArticle[];
  total: number;
  statistics: NewsStatistics;
}

// Helper to convert Firestore Timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

// Convert Firestore data to NewsArticle
const convertFirestoreData = (data: unknown, id: string, municipalityId: string): NewsArticle => {
  const docData = data as Record<string, unknown>;
  
  // Convert translations to proper format
  const translationsData = docData.translations as Record<string, { title?: string; content?: string; summary?: string }> | undefined;
  const translations: Record<string, { title: string; content: string }> = {};
  if (translationsData) {
    Object.keys(translationsData).forEach((lang) => {
      const trans = translationsData[lang];
      if (trans) {
        translations[lang] = {
          title: trans.title || '',
          content: trans.content || '',
        };
      }
    });
  }

  return {
    id,
    municipalityId,
    translations,
    category: (docData.category as NewsArticle['category']) || NewsCategory.NEWS,
    status: (docData.status as NewsStatus) || NewsStatus.DRAFT,
    visibility: (docData.visibility as NewsArticle['visibility']) || 'PUBLIC',
    featuredImage: typeof docData.featuredImage === 'string' ? docData.featuredImage : undefined,
    attachments: (docData.attachments as string[]) || [],
    publishDate: docData.publishDate ? toDate(docData.publishDate) : undefined,
    isPinned: (docData.isPinned as boolean) || false,
    isFeatured: (docData.isFeatured as boolean) || false,
    slug: (docData.slug as string) || '',
    tags: (docData.tags as string[]) || [],
    viewCount: (docData.viewCount as number) || 0,
    uniqueViewCount: (docData.uniqueViewCount as number) || 0,
    averageReadTime: (docData.averageReadTime as number) || 0,
    authorId: (docData.authorId as string) || '',
    authorName: (docData.authorName as string) || '',
    createdAt: toDate(docData.createdAt),
    updatedAt: toDate(docData.updatedAt),
    publishedAt: docData.publishedAt ? toDate(docData.publishedAt) : undefined,
    pushNotificationSent: (docData.pushNotificationSent as boolean) || false,
    pushNotificationSentAt: docData.pushNotificationSentAt ? toDate(docData.pushNotificationSentAt) : undefined,
  };
};

export const useMunicipalityNews = (municipalityId: string | null) => {
  const [data, setData] = useState<NewsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      setData(null);
      return;
    }

    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);

        const newsCollection = collection(db, 'municipalities', municipalityId, 'news');
        const snapshot = await getDocs(query(newsCollection, orderBy('createdAt', 'desc')));

        const items: NewsArticle[] = [];
        let totalViews = 0;
        let publishedCount = 0;
        let draftCount = 0;
        let archivedCount = 0;
        let pinnedCount = 0;
        let featuredCount = 0;

        snapshot.forEach((doc) => {
          const newsItem = convertFirestoreData(doc.data(), doc.id, municipalityId);
          items.push(newsItem);

          // Calculate statistics
          totalViews += newsItem.viewCount || 0;
          if (newsItem.status === NewsStatus.PUBLISHED) publishedCount++;
          else if (newsItem.status === NewsStatus.DRAFT) draftCount++;
          else if (newsItem.status === NewsStatus.ARCHIVED) archivedCount++;
          if (newsItem.isPinned) pinnedCount++;
          if (newsItem.isFeatured) featuredCount++;
        });

        const total = items.length;
        const averageViews = total > 0 ? Math.round(totalViews / total) : 0;

        setData({
          items,
          total,
          statistics: {
            total,
            published: publishedCount,
            draft: draftCount,
            archived: archivedCount,
            totalViews,
            averageViews,
            pinned: pinnedCount,
            featured: featuredCount,
          },
        });
      } catch (err) {
        console.error('Error fetching municipality news:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch municipality news');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [municipalityId]);

  return { data, loading, error };
};
